import { useEffect, useState } from "react";
import { Scale, Check, X } from "lucide-react";
import { toast } from "sonner";
import { appApi, inr, fmtDate, formatApiErrorDetail } from "@/lib/api";

const wd = (o) => o.weight_discrepancy || {};

export default function WeightReview() {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () =>
    appApi.get("orders").then((d) =>
      setOrders((d.orders || []).filter((o) => String(o.status).toLowerCase().includes("weight_discrepancy")))
    ).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const review = async (o, decision) => {
    const label = decision === "approve" ? "approve the extra charge" : "reject this discrepancy";
    if (!window.confirm(`${decision === "approve" ? "Approve" : "Reject"} weight discrepancy on ${o.awb}? This will ${label}.`)) return;
    setBusy(o.id + decision);
    try {
      await appApi.post(`orders/${o.id}/weight-discrepancy/review`, { decision });
      toast.success(decision === "approve" ? `Approved — extra charge deducted for ${o.awb}.` : `Discrepancy rejected for ${o.awb}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="weight-review-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Weight Review</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Weight discrepancies</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Couriers re-weigh parcels at their hubs. Approve the corrected charge or reject it with one tap — synced with the app.
        </p>
      </div>

      <div className="space-y-4" data-testid="weight-list">
        {(orders || []).map((o) => {
          const w = wd(o);
          const declared = w.declared_weight_kg ?? o.package?.weight_kg;
          const charged = w.charged_weight_kg ?? w.courier_weight_kg;
          const extra = w.extra_charge_inr ?? w.extra_amount_inr;
          const pending = String(o.status).toLowerCase() === "weight_discrepancy_pending";
          return (
            <div key={o.id} className={`border-2 border-obsidian bg-white p-6 ${pending ? "shadow-[6px_6px_0px_0px_#2563EB]" : ""}`} data-testid={`weight-card-${o.awb}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 bg-obsidian text-sky-300 flex items-center justify-center"><Scale className="w-5 h-5" /></span>
                  <div>
                    <p className="font-mono text-sm font-bold">{o.awb}</p>
                    <p className="text-xs text-zinc-500">{o.courier_display_name || o.courier_name} · {fmtDate(o.created_at)}</p>
                  </div>
                </div>
                <span className={`text-[11px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 ${pending ? "bg-acid text-white" : "bg-zinc-200 text-obsidian"}`}>
                  {String(o.status).replace(/_/g, " ")}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-5 max-w-xl">
                <div className="border border-zinc-200 p-4">
                  <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400">Declared</p>
                  <p className="font-display font-black text-2xl tracking-tight mt-1">{declared ?? "—"} kg</p>
                </div>
                <div className="border border-zinc-200 p-4">
                  <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400">Courier measured</p>
                  <p className="font-display font-black text-2xl tracking-tight mt-1">{charged ?? "—"} kg</p>
                </div>
                <div className="border-2 border-obsidian p-4 bg-paper">
                  <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400">Extra charge</p>
                  <p className="font-display font-black text-2xl tracking-tight mt-1">{extra != null ? inr(extra) : "—"}</p>
                </div>
              </div>
              {pending && (
                <div className="flex gap-3 mt-5 flex-wrap">
                  <button onClick={() => review(o, "approve")} disabled={busy} data-testid={`weight-approve-${o.awb}`}
                    className="inline-flex items-center gap-2 bg-acid text-white text-xs font-bold uppercase tracking-widest px-5 py-3 border-2 border-obsidian shadow-[3px_3px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 disabled:opacity-60">
                    <Check className="w-4 h-4" /> {busy === o.id + "approve" ? "Working…" : "Approve charge"}
                  </button>
                  <button onClick={() => review(o, "reject")} disabled={busy} data-testid={`weight-reject-${o.awb}`}
                    className="inline-flex items-center gap-2 bg-white text-red-600 text-xs font-bold uppercase tracking-widest px-5 py-3 border-2 border-red-600 hover:bg-red-50 transition-colors duration-150 disabled:opacity-60">
                    <X className="w-4 h-4" /> {busy === o.id + "reject" ? "Working…" : "Reject"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {orders && orders.length === 0 && (
          <div className="border-2 border-obsidian bg-white p-10 flex items-center gap-4" data-testid="weight-empty">
            <span className="w-10 h-10 bg-acid text-white flex items-center justify-center"><Scale className="w-5 h-5" /></span>
            <p className="text-sm text-zinc-600">No weight discrepancies on your shipments. Declared and measured weights match.</p>
          </div>
        )}
        {!orders && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Scanning shipments…</p>}
      </div>
    </div>
  );
}
